import React, { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import LogoImg from "../../../assets/icons/logo.svg";
import PharmacyLoginImg from "../../../components/IconComp/pharmacyloginImg.svg";
import PhoneInput from "react-phone-input-2";
import * as Yup from "yup";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import { LOGIN_SCREEN_ROUTE, START_SCREEN_ROUTE } from "../../../routes";
import AppButton from "../../common/AppButton";
import LoadingSpinner from "../../common/Loading";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";

import {
  login,
  signup,
  verifySignUpOTP,
} from "../../../features/auth/authSlice";
import SignUpOtpScreen from "./signupOtpScreen";
import SignUpCreatePassword from "./signupCreatePass";
import SignUpDetail from "./signupDetail";
import BackIcon from "../../IconComp/backIcon";
import AppSnackbar from "../../common/AppSnackbar";
import DisableModal from "../../packages/disableModel";
import SuccessModal from "../../packages/successModal";

// Validation schema

const basicInfoValidationSchema: any = Yup.object({
  email: Yup.string().email("Invalid email format").required("Required*"),
  phone_number: Yup.string()
    .required("Required*")
    .test("valid-phone", "Invalid phone number", (value) => {
      if (!value) return true; // Skip if empty (Required* will handle this)
      return isValidPhoneNumber(value, "PK"); // Adjust the country code as needed
    }),
  country: Yup.string()
    .matches(/^[a-zA-Z\s]+$/, "Only Characters are allowed")
    .required("Required*"),
  city: Yup.string().required("Required*"),
  address: Yup.string().required("Required*"),
});

const detailValidationSchema: any = Yup.object({
  name: Yup.string()
    //.matches(/^[a-zA-Z\s]+$/, "Only Characters are allowed")
    //.matches(/^[^\d]+$/, "Numbers are not allowed")
    .required("Required*"),
  contact_number: Yup.string()
    .matches(/^\d+$/, "Only numbers are allowed") // Regex to allow only digits
    .required("Required*"),
  //operating_hours: Yup.string().required("Required*"),
});

const passwordValidationSchema: any = Yup.object({
  password: Yup.string()
    .required("Required*")
    .test(
      "password-strength",
      "Password must be at least 6 characters long and include at least one number and one character",
      (value) => {
        if (!value) return true; // If the field is empty, show "Required*" message
        return /^(?=.*[0-9])(?=.*[^\d]).{6,}$/.test(value); // Minimum length 6, at least one number, and one non-digit character
      }
    ),
  confirmPassword: Yup.string().when("password", {
    is: (password: any) => password && password.length > 0, // Only validate if password is filled
    then: Yup.string()
      .required("Required*")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
    otherwise: Yup.string().notRequired(), // No validation if password is not filled
  }),
});

const initialValues = {
  name: "",
  email: "",
  phone_number: "",
  password: "",
  confirmPassword: "",
  logo: "",
  pharmacy_reg: "",
  license_number: "",
  license_expire_date: "",
  country: "",
  city: "",
  address: "",
  contact_number: "",
  operating_hours: "",
};
const SignUpForm = () => {
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const { isLoading, isLoginError, loginMessage } = useAppSelector(
    (state: any) => state?.auth
  );
  const dispatch = useAppDispatch();
  const [isDetail, setIsDetail] = useState(false);
  const [isPassword, setIsPassword] = useState(false);
  const [isOtpScreen, setIsOtpScreen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [value, setValue] = useState(initialValues);
  const [fileName, setFileName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [signupError, setSignupError] = useState<any>("");
  const [otpError, setOtpError] = useState<any>("");
  const [errors, setErrors] = useState(initialValues);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [openSuccessModel, setOpenSuccessModel] = useState(false);

  const handleOpenSuccessModal = () => {
    setOpenSuccessModel(true);
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModel(false);
  };

  const handleContinue = async () => {
    try {
      await basicInfoValidationSchema.validate(value, { abortEarly: false });
      setErrors(initialValues); // Clear errors on successful validation
      setIsDetail(true);
    } catch (validationErrors: any) {
      const errorMessages: any = {};
      validationErrors.inner.forEach((err: any) => {
        errorMessages[err.path] = err.message;
      });
      setErrors(errorMessages);
    }
  };
  const handleContinuePass = async () => {
    try {
      await detailValidationSchema.validate(value, { abortEarly: false });
      if (!termsAccepted) {
        return; // Stop further execution
      }
      setErrors(initialValues); // Clear errors on successful validation
      setIsDetail(false);
      setIsPassword(true);
    } catch (validationErrors: any) {
      const errorMessages: any = {};
      validationErrors.inner.forEach((err: any) => {
        errorMessages[err.path] = err.message;
      });
      setErrors(errorMessages);
    }
  };

  const handleSubmit = async () => {
    try {
      await passwordValidationSchema.validate(value, { abortEarly: false });
      setErrors(initialValues); // Clear errors on successful validation

      const formData: any = new FormData();
      const subscription_type = localStorage.getItem("subscription_type");
      // const is_free_trail = localStorage.getItem("is_free_trail");
      const signUpData = {
        name: value?.name,
        email: value?.email,
        is_free_trail: subscription_type,
        phone_number: value?.phone_number,
        password: value?.password,
        logo: value?.logo,
        pharmacy_reg: value.pharmacy_reg ? value.pharmacy_reg : undefined,
        license_number: value?.license_number
          ? value?.license_number
          : undefined,
        license_expire_date: value?.license_expire_date
          ? value?.license_expire_date
          : undefined,
        country: value?.country,
        city: value?.city,
        address: value?.address,
        contact_number: value?.contact_number,
        operating_hours: value?.operating_hours
          ? value?.operating_hours
          : undefined,
      };
      Object?.entries(signUpData)?.forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
          formData?.append(key, val); // Append key-value pairs to formData
        }
      });

      // Dispatch the API call with the formData
      dispatch(signup(formData))?.then((res: any) => {
        if (res?.payload?.success === true) {
          const subscription_type = localStorage.getItem("subscription_type");

          // Navigate to OTP Screen with state
          setPhone(res?.payload?.data?.phone_number);
          setEmail(res?.payload?.data?.email);
          setIsOtpScreen(true);
        } else {
          setSignupError(res?.payload);
        }
      });
    } catch (validationErrors: any) {
      // Collect validation errors and set them in the state
      const errorMessages: any = {};
      validationErrors.inner.forEach((err: any) => {
        errorMessages[err.path] = err.message;
      });
      setErrors(errorMessages);
    }
  };

  const handleVerifyOtp = async (otpString: string) => {
    try {
      await passwordValidationSchema.validate(value, { abortEarly: false });
      setErrors(initialValues); // Clear errors on successful validation
      setOtpError("");
      const otpData: any = {
        otp: otpString,
        email: email,
        phone_number: phone,
      };
      // Dispatch the API call with the formData
      dispatch(verifySignUpOTP(otpData))?.then((res: any) => {
        if (res?.payload?.success === true) {
          // open success model
          handleOpenSuccessModal();
          // navigate to login
          //navigate(LOGIN_SCREEN_ROUTE);
        } else {
          setOtpError(res?.payload);
          console.error("Login API failed:", res?.payload || "Unknown error");
        }
      });
    } catch (validationErrors: any) {
      // Collect validation errors and set them in the state
      const errorMessages: any = {};
      validationErrors.inner.forEach((err: any) => {
        errorMessages[err.path] = err.message;
      });
      setErrors(errorMessages);
    }
  };
  const getOtpAsString = () => otp.join("");

  const isMobile = useMediaQuery("(max-width: 1022px)");

  const handlePhoneNumberChange = (PhoneNo: any) => {
    const phone_no = PhoneNo.target.value;

    const val = phone_no.replace(/[\s-+()]/g, "");
    const phoneRegExp = /^((0)?)(3)([0-9]{9})$/gm;
    const res = phoneRegExp.test(val);
    // handlePatientName(phone_no.replace(/[\s-+()]/g, ""));
    if (res) {
      setValue({
        ...value,
        phone_number: phone_no.replace(/[\s-+()]/g, ""),
      });
    }
  };
  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      // Allow only numeric input
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to the next box if value is entered
      if (value && index < otp.length - 1) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (!otp[index] && index > 0) {
        // Move focus to the previous box if empty and Backspace is pressed
        const prevInput = document.getElementById(`otp-input-${index - 1}`);
        if (prevInput) prevInput.focus();
        newOtp[index - 1] = "";
      } else {
        newOtp[index] = "";
      }
      setOtp(newOtp);
    }
  };
  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      setValue((prev: any) => ({
        ...prev,
        logo: file.name, // Set the file name to the logo field
      }));
      setFileName(file.name); // Update the file name state
    }
  };
  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setValue((prev: any) => ({ ...prev, [name]: value }));

    // Determine which schema to use based on the field's name
    let fieldValidationSchema: any;

    if (basicInfoValidationSchema.fields[name]) {
      fieldValidationSchema = basicInfoValidationSchema.fields[name];
    } else if (detailValidationSchema.fields[name]) {
      fieldValidationSchema = detailValidationSchema.fields[name];
    } else if (passwordValidationSchema.fields[name]) {
      fieldValidationSchema = passwordValidationSchema.fields[name];
    }

    // If the validation schema for the field is found
    if (fieldValidationSchema) {
      fieldValidationSchema
        .validate(value)
        .then(() => {
          // Clear the error for the field if validation is successful
          setErrors((prevErrors: any) => {
            const { [name]: _, ...restErrors } = prevErrors; // Remove the error for the field
            return restErrors;
          });
        })
        .catch((err: any) => {
          // Set error for the field if validation fails
          setErrors((prevErrors: any) => ({
            ...prevErrors,
            [name]: err.message,
          }));
        });
    }
  };
  const handleBackToSignup = () => {
    setIsDetail(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        backgroundColor: "#fff",
      }}
    >
      {/* Left Section - Form */}
      <Box
        component="img"
        src={PharmacyLoginImg}
        alt="login_img"
        sx={{
          width: "100%", // Default for smaller screens
          "@media (min-width: 1024px) and (max-width: 1400px)": {
            width: "55%", // Adjusted width for screens between 1024px and 1400px
          },
          "@media (min-width: 1400px) and (max-width: 1600px)": {
            width: "65%", // Adjusted width for screens larger than 1400px
          },
          "@media (min-width: 1600px) ": {
            width: "70%", // Adjusted width for screens larger than 1400px
          },
          p: isMobile ? 2 : 6,
          backgroundImage: "linear-gradient(180deg, #0196DA 0%, #025BAB 100%)",
          height: "auto", // Use auto to adapt to the content
          minHeight: isMobile ? "0" : "100vh", // Ensure it covers the container
          backgroundSize: "cover", // Ensures background image fits properly
          backgroundPosition: "center", // Centers the background image
        }}
      />

      {/* Right Section - Illustration */}
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: 4,
          backgroundColor: "#fff",
        }}
      >
        {isDetail && ( // Conditionally render the back icon
          <Box
            sx={{
              width: "25%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              position: "relative",
              padding: "5px",
            }}
          >
            <Box
              sx={{
                cursor: "pointer",
                position: "absolute",
                top: 40,
                left: 22,
              }}
              onClick={handleBackToSignup}
            >
              <BackIcon />
            </Box>
          </Box>
        )}{" "}
        <Box
          sx={{
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
            flexDirection: "column",
            pt: 7,
          }}
        >
          <Box
            component="img"
            src={LogoImg}
            alt="logo"
            sx={{ width: "142px", cursor: "pointer" }}
            onClick={() => navigate(START_SCREEN_ROUTE)}
          />
          <Box sx={{ fontSize: "18px", pt: 1, mb: 8 }}>
            Pharmacy Management System
          </Box>
        </Box>
        {isOtpScreen ? (
          <SignUpOtpScreen
            value={value}
            otp={otp}
            handleChange={handleChange}
            handleKeyDown={handleKeyDown}
            handleVerifyOtp={handleVerifyOtp}
            getOtpAsString={getOtpAsString}
            handleSubmit={handleSubmit}
            otpError={otpError}
          />
        ) : isPassword ? (
          <SignUpCreatePassword
            value={value}
            showPassword={showPassword}
            handleInputChange={handleInputChange}
            errors={errors}
            setShowPassword={setShowPassword}
            showConfirmPassword={showConfirmPassword}
            setShowConfirmPassword={setShowConfirmPassword}
            handleSubmit={handleSubmit}
            signupError={signupError}
          />
        ) : isDetail ? (
          <SignUpDetail
            value={value}
            errors={errors}
            handleInputChange={handleInputChange}
            handleImageChange={handleImageChange}
            fileName={fileName}
            handleContinuePass={handleContinuePass}
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
          />
        ) : (
          <>
            <Grid
              container
              spacing={2}
              style={{
                paddingLeft: isMobile ? "0px" : "30px",
                paddingRight: isMobile ? "0px" : "30px",
              }}
            >
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  name="email"
                  value={value.email}
                  onChange={handleInputChange}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px", // Set the total height of the input field
                      padding: "0", // Remove default padding
                      display: "flex",
                      alignItems: "center", // Center the input content
                      justifyContent: "center",
                    },
                    "& .MuiFormLabel-root": {
                      top: "-5px", // Adjust label positioning if necessary
                    },
                    "& .MuiInputBase-input": {
                      padding: "0 10px", // Add inner padding for input
                      height: "100%", // Fill the container's height
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <PhoneInput
                  //onlyCountries={["pk"]}
                  country={"pk"}
                  value={value.phone_number}
                  onBlur={handlePhoneNumberChange}
                  onChange={(phone: any) => {
                    setValue((prev) => ({ ...prev, phone_number: phone }));
                    // Clear the error when the user starts entering a phone number
                    if (errors.phone_number) {
                      setErrors((prev) => ({ ...prev, phone_number: "" }));
                    }
                  }}
                  inputStyle={{
                    width: "100%",
                    height: "40px",
                    fontSize: "13px",
                    paddingLeft: "48px",
                    borderRadius: "5px",
                  }}
                  buttonStyle={{ borderRadius: "5px 0 0 5px" }}
                  //dropdownStyle={{ width: "500px" }}
                />
                {errors.phone_number && (
                  <p
                    style={{
                      color: "#CB2C2C",
                      fontSize: "12px",
                      marginLeft: "15px",
                    }}
                  >
                    {errors.phone_number}
                  </p>
                )}
                {/* error={Boolean(errors.phone_number)}
                helperText={errors.phone_number} */}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Country"
                  name="country"
                  value={value.country}
                  onChange={handleInputChange}
                  error={Boolean(errors.country)}
                  helperText={errors.country}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px", // Set the total height of the input field
                      padding: "0", // Remove default padding
                      display: "flex",
                      alignItems: "center", // Center the input content
                      justifyContent: "center",
                    },
                    "& .MuiFormLabel-root": {
                      top: "-5px", // Adjust label positioning if necessary
                    },
                    "& .MuiInputBase-input": {
                      padding: "0 10px", // Add inner padding for input
                      height: "100%", // Fill the container's height
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="City"
                  name="city"
                  value={value.city}
                  onChange={handleInputChange}
                  error={Boolean(errors.city)}
                  helperText={errors.city}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px", // Set the total height of the input field
                      padding: "0", // Remove default padding
                      display: "flex",
                      alignItems: "center", // Center the input content
                      justifyContent: "center",
                    },
                    "& .MuiFormLabel-root": {
                      top: "-5px", // Adjust label positioning if necessary
                    },
                    "& .MuiInputBase-input": {
                      padding: "0 10px", // Add inner padding for input
                      height: "100%", // Fill the container's height
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Address"
                  name="address"
                  value={value.address}
                  onChange={handleInputChange}
                  error={Boolean(errors.address)}
                  helperText={errors.address}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px", // Set the total height of the input field
                      padding: "0", // Remove default padding
                      display: "flex",
                      alignItems: "center", // Center the input content
                      justifyContent: "center",
                    },
                    "& .MuiFormLabel-root": {
                      top: "-5px", // Adjust label positioning if necessary
                    },
                    "& .MuiInputBase-input": {
                      padding: "0 10px", // Add inner padding for input
                      height: "100%", // Fill the container's height
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <AppButton
                    title="Continue"
                    fullWidth
                    sx={{ mb: 2 }}
                    onClick={handleContinue}
                  />
                )}
              </Grid>
            </Grid>
          </>
        )}
        {isOtpScreen ? null : (
          <Grid container justifyContent="center">
            <Box sx={{ pb: 2, fontSize: "16px", fontWeight: 400 }}>
              Already have an account?{" "}
              <Link
                to={LOGIN_SCREEN_ROUTE}
                //state={{ state: "clinic_owner" }}
              >
                <span
                  style={{
                    color: "#0055A8",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Login
                </span>
              </Link>
            </Box>
          </Grid>
        )}
      </Container>
      <SuccessModal open={openSuccessModel} onClose={handleCloseSuccessModal} />
    </Box>
  );
};

export default SignUpForm;
